/**
 * ExpressRegister — lightweight ad-conversion landing.
 *
 * Purpose: a single-screen, low-friction destination for Google Ads traffic
 * (replaces sending ad clicks straight into the 5-step /register wizard, which
 * stays parked & fully intact). Captures a high-intent lead in the CRM WITH
 * marketing attribution (gclid + UTMs) via /api/enquiries/demo-request, then
 * grants instant demo access.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Loader2, CheckCircle2, ShieldCheck, Sparkles, Clock, MapPin, Lock,
} from 'lucide-react';
import CorvanaLogo from '@/components/CorvanaLogo';
import { API_URL } from '@/config/apiConfig';
import { getAttribution } from '@/utils/attribution';
import { INDUSTRY_CATEGORIES } from '@/config/industryConfig';

const BENEFITS = [
  { icon: Clock, title: 'Live in 60 seconds', text: 'No credit card. See your dashboard instantly.' },
  { icon: MapPin, title: 'Built for Australian business', text: 'ABR-verified data, GST-ready, local support.' },
  { icon: ShieldCheck, title: 'Bank-grade security', text: 'Your data is encrypted and never sold.' },
];

// A/B headline variants — pick with ?v=a|b|c. Each lead's `source` records the
// variant (express_register_<v>) so the CRM source filter shows which converts best.
const VARIANTS = {
  a: {
    lead: 'Your business,',
    accent: 'clearer than ever',
    sub: "Tell us a little about your business and we'll spin up your Corvana workspace — real insights from real Australian data, in under a minute.",
  },
  b: {
    lead: 'Stop guessing.',
    accent: 'Start knowing.',
    sub: "AI-powered insights from real Australian business data. See what's actually driving your numbers — in under a minute, no spreadsheets.",
  },
  c: {
    lead: 'Your numbers,',
    accent: 'finally making sense',
    sub: 'Corvana turns your business data into clear, daily decisions. No jargon, no analysts — just answers you can act on today.',
  },
};

// Fire a conversion signal for Google Ads / GA4 (via GTM dataLayer + gtag).
// Wire a Google Ads conversion action to the `express_lead` dataLayer event in
// GTM, or set REACT_APP_GOOGLE_ADS_CONVERSION="AW-XXXXX/label" to fire directly.
function fireLeadConversion({ variant, attribution }) {
  if (typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'express_lead',
      form: 'express_register',
      ab_variant: variant,
      ...attribution,
    });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', { form: 'express_register', ab_variant: variant });
      const sendTo = process.env.REACT_APP_GOOGLE_ADS_CONVERSION;
      if (sendTo) window.gtag('event', 'conversion', { send_to: sendTo });
    }
  } catch { /* analytics must never break the flow */ }
}

export default function ExpressRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variant = (VARIANTS[(searchParams.get('v') || 'a').toLowerCase()] ? (searchParams.get('v') || 'a').toLowerCase() : 'a');
  const copy = VARIANTS[variant];
  const formRef = useRef(null);
  const firstNameRef = useRef(null);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', company: '', phone: '', industry: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    document.title = 'Get Started — Corvana';
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => firstNameRef.current?.focus(), 300);
  };

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid work email';
    if (!form.company.trim()) next.company = 'Required';
    if (!form.industry) next.industry = 'Select your industry';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const res = await fetch(`${API_URL}/api/enquiries/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'demo_request',
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          phone: form.phone,
          company: form.company,
          industry: form.industry,
          source: `express_register_${variant}`,
          ...attribution,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        sessionStorage.setItem('demo_access_granted', 'true');
        sessionStorage.setItem('demo_access_email', form.email);
        fireLeadConversion({ variant, attribution });
        setDone(true);
      } else if (res.status === 429) {
        setServerError("You've tried a few times just now — please wait a minute and try again.");
      } else {
        setServerError(data.message || data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setServerError('Unable to submit right now. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white relative overflow-hidden" data-testid="express-register-page">
      {/* Ambient brand glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-emerald-500/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-[#0055B5]/25 blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 lg:py-14 pb-28 lg:pb-14">
        <div className="flex items-center justify-between mb-8 lg:mb-16">
          <Link to="/" data-testid="express-logo-home">
            <CorvanaLogo size="md" variant="light" />
          </Link>
          <Link
            to="/crm-login"
            className="text-sm text-slate-300 hover:text-white transition-colors"
            data-testid="express-login-link"
          >
            Sign in
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 lg:gap-x-16 gap-y-8 lg:items-start">
          {/* Hook — badge + headline + subtext (mobile: 1st, above the form) */}
          <motion.div
            className="lg:col-start-1 lg:row-start-1"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Express setup
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-4">
              {copy.lead}{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-[#4f9dff] bg-clip-text text-transparent">
                {copy.accent}
              </span>
            </h1>
            <p className="text-base lg:text-lg text-slate-300 max-w-md">
              {copy.sub}
            </p>
          </motion.div>

          {/* Form (mobile: 2nd, right under the hook — the money element) */}
          <motion.div
            className="lg:col-start-2 lg:row-start-1 lg:row-span-2 w-full"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur-xl p-6 sm:p-9 shadow-2xl shadow-black/40">
              {!done ? (
                <>
                  <h2 className="text-lg font-semibold mb-1">Get started free</h2>
                  <p className="text-sm text-slate-400 mb-7">Takes less than a minute. No credit card required.</p>

                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" data-testid="express-register-form" noValidate>
                    <div className="grid grid-cols-2 gap-3">
                      <Field
                        label="First name" testid="express-first-name" value={form.firstName}
                        onChange={setField('firstName')} error={errors.firstName} autoFocus inputRef={firstNameRef}
                      />
                      <Field
                        label="Last name" testid="express-last-name" value={form.lastName}
                        onChange={setField('lastName')} optional
                      />
                    </div>
                    <Field
                      label="Work email" type="email" testid="express-email" value={form.email}
                      onChange={setField('email')} error={errors.email}
                    />
                    <Field
                      label="Business name" testid="express-company" value={form.company}
                      onChange={setField('company')} error={errors.company}
                    />
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">Industry</label>
                      <select
                        data-testid="express-industry"
                        value={form.industry}
                        onChange={setField('industry')}
                        className={`w-full rounded-xl bg-white/[0.06] border px-3.5 py-3 text-base text-white outline-none transition-colors focus:border-emerald-400/60 ${errors.industry ? 'border-red-400/60' : 'border-white/10'}`}
                      >
                        <option value="" className="bg-[#0a0e17]">Select your industry…</option>
                        {INDUSTRY_CATEGORIES.map((c) => (
                          <option key={c.id} value={c.name} className="bg-[#0a0e17]">{c.name}</option>
                        ))}
                      </select>
                      {errors.industry && <p className="text-xs text-red-400 mt-1">{errors.industry}</p>}
                    </div>
                    <Field
                      label="Phone" type="tel" testid="express-phone" value={form.phone}
                      onChange={setField('phone')} optional
                    />

                    {serverError && (
                      <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="express-error">
                        {serverError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="express-submit"
                      className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-[#0055B5] hover:from-emerald-400 hover:to-[#0066d6] text-white font-semibold py-3.5 transition-all disabled:opacity-60 shadow-lg shadow-emerald-500/20"
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting up…</> : <>Get started <ArrowRight className="w-4 h-4" /></>}
                    </button>

                    <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500 pt-1">
                      <Lock className="w-3 h-3" /> Your details are encrypted and never sold.
                    </p>
                  </form>

                  <div className="mt-6 pt-5 border-t border-white/10 text-center">
                    <Link to="/register" className="text-xs text-slate-400 hover:text-slate-200 transition-colors" data-testid="express-full-register-link">
                      Prefer the full guided setup? Continue to full registration →
                    </Link>
                  </div>
                </>
              ) : (
                <div className="text-center py-6" data-testid="express-success">
                  <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">You're all set{form.firstName ? `, ${form.firstName}` : ''}!</h2>
                  <p className="text-sm text-slate-400 max-w-sm mx-auto mb-7">
                    Your workspace is ready to explore. Our team will reach out shortly to
                    help tailor Corvana to {form.company || 'your business'}.
                  </p>
                  <button
                    onClick={() => navigate('/demo')}
                    data-testid="express-explore-demo"
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-[#0055B5] hover:from-emerald-400 hover:to-[#0066d6] text-white font-semibold py-3.5 transition-all shadow-lg shadow-emerald-500/20"
                  >
                    Explore your live demo <ArrowRight className="w-4 h-4" />
                  </button>
                  <Link to="/" className="inline-block mt-4 text-sm text-slate-400 hover:text-slate-200 transition-colors" data-testid="express-back-home">
                    Back to home
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Trust benefits (mobile: 3rd, below the form; desktop: under the hook) */}
          <motion.ul
            className="lg:col-start-1 lg:row-start-2 space-y-5 max-w-md"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {BENEFITS.map((b) => (
              <li key={b.title} className="flex items-start gap-4" data-testid={`express-benefit-${b.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/20 to-[#0055B5]/10 border border-emerald-500/20 shrink-0">
                  <b.icon className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{b.title}</div>
                  <div className="text-sm text-slate-400">{b.text}</div>
                </div>
              </li>
            ))}
          </motion.ul>
        </div>
      </div>

      {/* Sticky mobile CTA — always one tap from the form (hidden on desktop / after success) */}
      {!done && (
        <div
          className="lg:hidden fixed inset-x-0 bottom-0 z-30 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-[#0a0e17] via-[#0a0e17]/95 to-transparent"
          data-testid="express-sticky-cta"
        >
          <button
            type="button"
            onClick={scrollToForm}
            data-testid="express-sticky-cta-button"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-[#0055B5] text-white font-semibold py-3.5 shadow-lg shadow-emerald-500/30 active:scale-[0.99] transition-transform"
          >
            Get started free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, error, type = 'text', testid, optional, autoFocus, inputRef }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-300 mb-1.5">
        {label} {optional && <span className="text-slate-500 font-normal">(optional)</span>}
      </label>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        data-testid={testid}
        className={`w-full rounded-xl bg-white/[0.06] border px-3.5 py-3 text-base text-white placeholder-slate-500 outline-none transition-colors focus:border-emerald-400/60 ${error ? 'border-red-400/60' : 'border-white/10'}`}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}
